import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { NOTIFICATION_CATEGORIES, type NotificationCategory, type SlackConnectionView, type SlackEnvironmentRouting, type SlackRoutes } from "@starsach/shared-types";
import { useSaveSlackRoutes, useSlack, useSlackDisconnect, useSlackStart, useSlackTest } from "../../api/hooks";
import { Hint } from "../../components/Hint";
import { NotHeld } from "../../components/NotHeld";
import { Card, DataTable, Dialog, EmptyState, ErrorState, LoadingState, MutationError, RelativeTime, StatusPill, btnPrimary, btnSecondary, inputClass } from "../../components/ui";

/**
 * Access › Slack notifications (#568; crmarr/templates
 * customer-slack-notifications.md). The organization's channels, connected
 * with Add to Slack, and where this environment's notifications go - one
 * channel per kind, or Off. Connecting, testing, disconnecting and routing
 * need `notifications:route` (connecting from here: on this environment);
 * anyone with the section enabled reads it.
 */
export const SLACK_RESULT_MESSAGES: Record<string, { tone: "ok" | "warning"; text: string }> = {
  connected: { tone: "ok", text: "The Slack channel is connected." },
  canceled: { tone: "warning", text: "Slack was canceled before the channel was connected. Nothing was saved." },
  expired: { tone: "warning", text: "That took more than ten minutes, so nothing was saved. Add to Slack again." },
  invalid: { tone: "warning", text: "That connection was not started by you here, so nothing was saved. Add to Slack again." },
  forbidden: { tone: "warning", text: "You no longer hold connect and route on this environment, so nothing was saved." },
  failed: { tone: "warning", text: "Slack did not finish connecting. Nothing was saved; try Add to Slack again." },
  unavailable: { tone: "warning", text: "Slack isn't set up for this environment of Starsach yet." },
};

export function SlackSection({ environmentId }: { environmentId: string }) {
  const q = useSlack();
  const [params, setParams] = useSearchParams();
  const result = params.get("slack");
  const channelName = params.get("channel");

  if (q.isPending) return <LoadingState label="Loading Slack notifications" />;
  if (q.isError) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  const env = q.data.environments.find((e) => e.environmentId === environmentId);
  const notice = result ? SLACK_RESULT_MESSAGES[result] : undefined;
  const broken = q.data.connections.filter((c) => c.status === "broken");

  return (
    <div className="space-y-4">
      {notice && (
        <div role="status" className={`flex items-start justify-between gap-3 rounded-xl border p-4 text-sm ${notice.tone === "ok" ? "border-green-200 bg-green-50 text-green-900 dark:border-green-900 dark:bg-green-950 dark:text-green-100" : "border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-100"}`}>
          <span>{notice.text}{result === "connected" && channelName ? ` Starsach will post to ${channelName}.` : ""}</span>
          <button type="button" className="text-xs underline" onClick={() => setParams({}, { replace: true })}>Dismiss</button>
        </div>
      )}
      {broken.length > 0 && (
        <div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-900 dark:border-red-900 dark:bg-red-950 dark:text-red-100">
          Slack stopped accepting messages for {broken.map((c) => c.channel ?? "a channel").join(", ")}. Nothing is posted there until someone reconnects it; the other channels carry on.
        </div>
      )}
      {!q.data.available ? (
        <Card title="Slack notifications">
          <EmptyState title="Slack isn't set up for this environment of Starsach yet" hint="Nothing needs doing on your side. Notifications still go by email." />
        </Card>
      ) : (
        <Channels connections={q.data.connections} environmentId={environmentId} canConnect={Boolean(env?.canRoute)} canManage={q.data.canConnect} />
      )}
      {env ? <Routing env={env} connections={q.data.connections} /> : <EmptyState title="This environment's routing is not available to you" hint="No role you hold here enables Slack notifications." />}
    </div>
  );
}

function Channels({ connections, environmentId, canConnect, canManage }: { connections: SlackConnectionView[]; environmentId: string; canConnect: boolean; canManage: boolean }) {
  const start = useSlackStart();
  const test = useSlackTest();
  const disconnect = useSlackDisconnect();
  const [tested, setTested] = useState<string>();
  const [leaving, setLeaving] = useState<SlackConnectionView | null>(null);
  const addToSlack = () => start.mutate(environmentId, { onSuccess: (r) => window.location.assign(r.url) });

  return (
    <Card title={<>Connected Slack channels<Hint id="access.slack-channels" label="how a channel is connected" /></>}>
      <p className="mb-3 text-sm text-neutral-600 dark:text-neutral-400">
        Add to Slack opens Slack, where you pick a channel - private channels you belong to are offered too. Starsach can post to that channel and read nothing in your Slack.
      </p>
      <MutationError of={[start, test, disconnect]} className="mb-3" />
      {tested && !test.isError && <p role="status" className="mb-3 text-sm text-green-700 dark:text-green-300">Test message sent to {tested}.</p>}
      <DataTable
        caption="Slack channels connected to your organization"
        rows={connections}
        rowKey={(c) => c.connectionId}
        sortBy={(c) => c.channel ?? ""}
        filter={connections.length > 3}
        empty={<EmptyState title="No Slack channel connected yet" hint="Until one is, notifications go by email only." />}
        columns={[
          { key: "channel", header: "Channel", render: (c) => <span><span className="font-medium">{c.channel ?? "A channel"}</span>{c.teamName && <span className="block text-xs text-neutral-500">{c.teamName}</span>}</span> },
          { key: "by", header: "Connected by", render: (c) => <span className="text-sm">{c.connectedBy}<span className="block text-xs text-neutral-500"><RelativeTime iso={c.connectedAt} /></span></span> },
          { key: "status", header: "Status", render: (c) => <span><StatusPill status={c.status} tone={c.status === "connected" ? "ok" : "critical"} />{c.brokenReason && <span className="block text-xs text-neutral-500">Slack said {c.brokenReason}</span>}</span> },
          {
            key: "delivery",
            header: "Last delivery",
            render: (c) => (c.lastDeliveryAt ? <span className="text-sm">{c.lastDeliveryOk ? "Succeeded" : "Failed"} <RelativeTime iso={c.lastDeliveryAt} />{!c.lastDeliveryOk && c.lastDeliveryError && <span className="block text-xs text-neutral-500">{c.lastDeliveryError}</span>}</span> : <span className="text-sm text-neutral-500">None yet</span>),
          },
          {
            key: "actions",
            header: "",
            render: (c) =>
              canManage ? (
                <span className="flex flex-wrap gap-2">
                  {c.status === "connected" ? (
                    <button type="button" className="text-sm underline" disabled={test.isPending} onClick={() => test.mutate(c.connectionId, { onSuccess: () => setTested(c.channel ?? "the channel") })} aria-label={`Send a test message to ${c.channel ?? "this channel"}`}>
                      Send test message
                    </button>
                  ) : (
                    canConnect && <button type="button" className="text-sm underline" onClick={addToSlack} aria-label={`Reconnect ${c.channel ?? "this channel"}`}>Reconnect</button>
                  )}
                  <button type="button" className="text-sm text-red-700 underline dark:text-red-400" onClick={() => setLeaving(c)} aria-label={`Disconnect ${c.channel ?? "this channel"}`}>Disconnect</button>
                </span>
              ) : null,
          },
        ]}
      />
      <div className="mt-4">
        {canConnect ? (
          <button type="button" className={btnPrimary} disabled={start.isPending} onClick={addToSlack}>
            {start.isPending ? "Opening Slack…" : connections.length === 0 ? "Add to Slack" : "Add another channel"}
          </button>
        ) : (
          <NotHeld permission="notifications:route" />
        )}
      </div>
      {leaving && (
        <Dialog title={`Disconnect ${leaving.channel ?? "this channel"}?`} onClose={() => setLeaving(null)}>
          <p className="text-sm">Starsach stops posting there at once. Notifications any environment sends to it read Off until they are routed to another channel; email is not affected.</p>
          <MutationError of={disconnect} className="mt-3" />
          <div className="mt-4 flex gap-2">
            <button type="button" className={btnSecondary} onClick={() => setLeaving(null)}>Cancel</button>
            <button type="button" className={btnPrimary} disabled={disconnect.isPending} onClick={() => disconnect.mutate(leaving.connectionId, { onSuccess: () => setLeaving(null) })}>
              {disconnect.isPending ? "Disconnecting…" : "Disconnect"}
            </button>
          </div>
        </Dialog>
      )}
    </Card>
  );
}

/** The page sends one channel per kind; the API stores lists, so two channels for one kind later is a page change. */
function draftOf(routes: SlackRoutes): Record<NotificationCategory, string> {
  return Object.fromEntries(NOTIFICATION_CATEGORIES.map((c) => [c.id, routes[c.id]?.[0] ?? ""])) as Record<NotificationCategory, string>;
}

function Routing({ env, connections }: { env: SlackEnvironmentRouting; connections: SlackConnectionView[] }) {
  const save = useSaveSlackRoutes(env.environmentId);
  const stored = useMemo(() => draftOf(env.routes), [env.routes]);
  const [draft, setDraft] = useState(stored);
  const [saved, setSaved] = useState(false);
  useEffect(() => setDraft(stored), [stored]);
  const dirty = NOTIFICATION_CATEGORIES.some((c) => draft[c.id] !== stored[c.id]);
  const readOnly = !env.canRoute;
  const nameOf = (id: string) => connections.find((c) => c.connectionId === id);

  const submit = () => {
    setSaved(false);
    const routes: SlackRoutes = Object.fromEntries(NOTIFICATION_CATEGORIES.filter((c) => draft[c.id]).map((c) => [c.id, [draft[c.id]]]));
    save.mutate(routes, { onSuccess: () => setSaved(true) });
  };

  return (
    <Card title={<>Which of {env.name}'s notifications go where<Hint id="access.slack-routing" label="routing to Slack" /></>}>
      {readOnly && <NotHeld permission="notifications:route" className="mb-3 block" />}
      {connections.length === 0 && !readOnly && <p className="mb-3 text-sm text-neutral-600 dark:text-neutral-400">Connect a channel above to route notifications to it.</p>}
      <MutationError of={save} className="mb-3" />
      <DataTable
        caption={`Slack routing for ${env.name}`}
        rows={[...NOTIFICATION_CATEGORIES]}
        rowKey={(c) => c.id}
        filter={false}
        empty={null}
        columns={[
          { key: "kind", header: "Notification", render: (c) => <span><span className="font-medium">{c.label}</span><span className="block text-xs text-neutral-500">{c.covers}</span></span> },
          {
            key: "channel",
            header: "Slack channel",
            render: (c) => {
              const current = draft[c.id];
              const gone = current && !nameOf(current);
              return (
                <select
                  aria-label={`Slack channel for ${c.label.toLowerCase()}`}
                  className={inputClass}
                  value={gone ? "" : current}
                  disabled={readOnly || connections.length === 0}
                  onChange={(e) => {
                    setSaved(false);
                    setDraft({ ...draft, [c.id]: e.target.value });
                  }}
                >
                  <option value="">Off</option>
                  {connections.map((x) => (
                    <option key={x.connectionId} value={x.connectionId}>
                      {(x.channel ?? "A channel") + (x.status === "broken" ? " (broken)" : "")}
                    </option>
                  ))}
                </select>
              );
            },
          },
        ]}
      />
      <p className="mt-3 text-xs text-neutral-500">Slack is sent as well as email, never instead of it. Each notice is posted once to its channel, however many people get it by email.{env.routed ? "" : " Nothing has been routed here yet, so every kind is Off."}</p>
      {!readOnly && (
        <div className="mt-4 flex items-center gap-2">
          <button type="button" className={btnPrimary} disabled={!dirty || save.isPending} onClick={submit}>{save.isPending ? "Saving…" : "Save changes"}</button>
          <button type="button" className={btnSecondary} disabled={!dirty || save.isPending} onClick={() => setDraft(stored)}>Cancel</button>
          {saved && !dirty && <span role="status" className="text-sm text-green-700 dark:text-green-300">Saved</span>}
        </div>
      )}
    </Card>
  );
}
